import { c as _c } from "react/compiler-runtime";
import type { CSSObject } from "@mui/material/styles";
import { type RefObject, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { prefersReducedMotion } from "#/shared/lib/prefersReducedMotion";
export type UseLandingRevealOptions = {
  /** Extra delay after intersecting (e.g. stagger between siblings). */
  staggerMs?: number;
};
type RevealState = "pending" | "reduce" | "static" | "hidden" | "revealed";
const hiddenSx: CSSObject = {
  opacity: 0,
  transform: "translate3d(0, 14px, 0)",
  transition: "none"
};
const inViewportLoose = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight;
  return rect.top < vh * 0.92 && rect.bottom > -40;
};

/**
 * Scroll-triggered entrance: `pending` is **fully visible** (matches SSR / first paint — no flash).
 * After layout: in-viewport → `static`; below-fold → `hidden` until IO, then `revealed`.
 * Respects `prefers-reduced-motion: reduce`.
 */
export const useLandingReveal = (ref, t0) => {
  const $ = _c(14);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    staggerMs: t2
  } = options;
  const staggerMs = t2 === undefined ? 0 : t2;
  const [state, setState] = useState("pending");
  let t3;
  let t4;
  if ($[2] !== ref) {
    t3 = () => {
      const el = ref.current;
      if (!el) {
        return;
      }
      if (prefersReducedMotion()) {
        setState("reduce");
        return;
      }
      if (inViewportLoose(el)) {
        setState("static");
      } else {
        setState("hidden");
      }
    };
    t4 = [ref];
    $[2] = ref;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useLayoutEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== ref || $[6] !== staggerMs || $[7] !== state) {
    t5 = () => {
      if (state !== "hidden") {
        return;
      }
      const el_0 = ref.current;
      if (!el_0) {
        return;
      }
      if (typeof IntersectionObserver !== "function") {
        setState("revealed");
        return;
      }
      const observer = new IntersectionObserver(entries => {
        const hit = entries.some(_temp);
        if (!hit) {
          return;
        }
        observer.disconnect();
        if (staggerMs > 0) {
          window.setTimeout(() => setState("revealed"), staggerMs);
        } else {
          setState("revealed");
        }
      }, {
        root: null,
        rootMargin: "0px 0px -6% 0px",
        threshold: 0
      });
      observer.observe(el_0);
      return () => observer.disconnect();
    };
    t6 = [ref, state, staggerMs];
    $[5] = ref;
    $[6] = staggerMs;
    $[7] = state;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  bb0: {
    if (state === "pending" || state === "reduce" || state === "static") {
      let t8;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = {};
        $[10] = t8;
      } else {
        t8 = $[10];
      }
      t7 = t8;
      break bb0;
    }
    if (state === "hidden") {
      t7 = hiddenSx;
      break bb0;
    }
    let t8;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        opacity: 1,
        transform: "translate3d(0, 0, 0)",
        transition: "opacity 0.55s cubic-bezier(0.22, 1, 0.36, 1), transform 0.55s cubic-bezier(0.22, 1, 0.36, 1)"
      };
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    t7 = t8;
  }
  const revealSx = t7;
  let t8;
  if ($[12] !== revealSx) {
    t8 = {
      revealSx
    };
    $[12] = revealSx;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};
function _temp(entry) {
  return entry.isIntersecting;
}